import { extends as _extends, objectWithoutProperties as _objectWithoutProperties } from '../../_virtual/_rollupPluginBabelHelpers.js';
import classNames from 'classnames';
import * as PropTypes from 'prop-types';
import * as React from 'react';
import { Icon } from '../Icon/Icon.js';
import { AccordionContext } from './AccordionContext.js';

var _excluded = ["buttonContent", "children", "className", "hasPictogram", "id", "onClick", "render", "shouldExpand", "targetContentId", "useButton", "useChevron", "context"];

/* eslint-disable @typescript-eslint/no-explicit-any */
function toggleElementStyleHeight(element, isExpand) {
  if (!(element instanceof HTMLElement)) {
    return;
  }
  var el = element;
  if (isExpand) {
    el.style.display = 'block';
    window.requestAnimationFrame(() => {
      var heightStyle = "".concat(el.scrollHeight, "px");
      el.style.height = heightStyle;
      el.addEventListener('transitionend', function expandListener() {
        el.removeEventListener('transitionend', expandListener, false);
        /* istanbul ignore else */
        if (el.style.height === heightStyle) {
          el.style.height = 'auto';
          el.style.overflow = 'visible';
        }
      }, false);
    });
  } else {
    window.requestAnimationFrame(() => {
      el.style.height = "".concat(el.scrollHeight, "px");
      window.requestAnimationFrame(() => {
        el.style.height = '0px';
        el.style.overflow = 'hidden';
        el.addEventListener('transitionend', function collapseListener() {
          el.removeEventListener('transitionend', collapseListener, false);
          /* istanbul ignore else */
          if (el.style.height === '0px') {
            el.removeAttribute('style');
          }
        }, false);
      });
    });
  }
}

/* eslint-disable @typescript-eslint/ban-types */

/* eslint-enable @typescript-eslint/ban-types */

var AccordionHeaderWithContext = props => {
  var {
      buttonContent = '',
      children = null,
      className,
      hasPictogram,
      id,
      onClick,
      render,
      shouldExpand,
      targetContentId,
      useButton,
      useChevron,
      context
    } = props,
    restProps = _objectWithoutProperties(props, _excluded);
  var shouldExpandProp = !!shouldExpand;
  var [isExpanded, setIsExpanded] = React.useState(false);
  var [targetContentNode, setTargetContentNode] = React.useState(null);
  var {
    collapseAll,
    expandedContentId,
    isAccordion,
    setExpandedContentId
  } = React.useContext(AccordionContext);

  // store target content pane Id
  React.useEffect(() => {
    setTargetContentNode(document.getElementById(targetContentId));
  }, [targetContentId]);

  // set 'isExpanded' state
  // in accordion mode - check if expandedContentId match taget content pane id
  // otherwise use 'shouldExpand' prop value (not accordion mode)
  React.useEffect(() => {
    var expandState = isAccordion ? expandedContentId === targetContentId : shouldExpandProp;
    setIsExpanded(expandState);
  }, [expandedContentId, isAccordion, shouldExpandProp, targetContentId]);

  // toggle targetContentNode expand
  React.useEffect(() => {
    if (targetContentNode) {
      toggleElementStyleHeight(targetContentNode, isExpanded);
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      targetContentNode.setAttribute('aria-hidden', "".concat(!isExpanded));
    }
  }, [targetContentNode, isExpanded]);

  // click handler wrapper
  var handlerClick = e => {
    if (!targetContentNode) {
      return;
    }
    var newExpandedState = !isExpanded;
    // eslint-disable-next-line consistent-return
    var updateAccordionState = () => {
      // eslint-disable-line consistent-return
      if (collapseAll) {
        return setIsExpanded(newExpandedState);
      }
      /* istanbul ignore else */
      if (expandedContentId !== targetContentId && setExpandedContentId) {
        setExpandedContentId(targetContentId);
        setIsExpanded(newExpandedState);
      }
    };
    var updateCollapsibleState = () => {
      setIsExpanded(newExpandedState);
    };
    if (isAccordion) {
      updateAccordionState();
    } else {
      updateCollapsibleState();
    }

    // trigger may be provided onClick
    /* istanbul ignore else */
    if (typeof onClick === 'function') {
      onClick(e, {
        isExpanded: newExpandedState
      });
    }
  };
  var classnames = classNames('accordion-header', {
    'accordion-header--clickable': useButton
  }, {
    'accordion-header--open': isExpanded
  }, // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  "".concat(className));
  var btnClassName = classNames('accordion-header__btn', 'hc-icon', {
    'withpictogram': hasPictogram
  }, {
    'hc-icon--control-accordion-close': isExpanded
  }, {
    'hc-icon--control-accordion-open': !isExpanded
  });
  var buttonAttributes = {
    'aria-controls': targetContentId,
    'aria-expanded': isExpanded,
    'aria-labelledby': id,
    onClick: handlerClick
  };
  var buttonBlock = /*#__PURE__*/React.createElement("button", _extends({
    type: "button",
    className: btnClassName
  }, buttonAttributes), buttonContent);

  // for 'render prop' pattern usage
  /* eslint-disable react/jsx-no-useless-fragment */
  if (typeof render === 'function') {
    return /*#__PURE__*/React.createElement("div", {
      className: classnames,
      id: id
    }, /*#__PURE__*/React.createElement(React.Fragment, null, render({
      handleChange: handlerClick,
      isExpanded
    }), useChevron && buttonBlock));
  }
  return useButton ? /*#__PURE__*/React.createElement("button", _extends({
    className: classnames,
    id: id,
    type: "button"
  }, buttonAttributes, restProps), children, useChevron && /*#__PURE__*/React.createElement(Icon, {
    className: classNames('chevron', {
      'withpictogram': hasPictogram
    }),
    symbol: isExpanded ? 'controlAccordionClose24' : 'controlAccordionOpen24'
  })) : /*#__PURE__*/React.createElement("div", _extends({
    className: classnames,
    id: id
  }, restProps), children, buttonBlock);
};
var AccordionHeader = props => /*#__PURE__*/React.createElement(AccordionContext.Consumer, null, context => /*#__PURE__*/React.createElement(AccordionHeaderWithContext, _extends({
  context: context
}, props)));
AccordionHeader.propTypes = {
  className: PropTypes.string,
  hasPictogram: PropTypes.bool,
  id: PropTypes.string.isRequired,
  render: PropTypes.func,
  shouldExpand: PropTypes.bool,
  targetContentId: PropTypes.string.isRequired,
  useButton: PropTypes.bool,
  useChevron: PropTypes.bool
};
AccordionHeader.defaultProps = {
  className: '',
  shouldExpand: false,
  useButton: true,
  useChevron: true
};

export { AccordionHeader, toggleElementStyleHeight };
